// API service layer for CyberInsti platform
// Auth is cookie-based (httpOnly `access_token` for JWT users + `session_token`
// for Google OAuth users). `credentials: 'include'` ensures cookies flow on
// every cross-site request to the backend preview domain.

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * XHR-based fallback for `fetch()`.
 *
 * Some 3rd-party scripts (PostHog session recording's network capture,
 * privacy-extensions, password managers etc) wrap `window.fetch` and try
 * to `postMessage()` the intercepted Fetch `Request` object across origins.
 * `Request` objects with bodies (POST + JSON) carry a ReadableStream that
 * can't be `structured-clone`d, so the wrapped fetch throws:
 *   "Failed to execute 'postMessage' on 'Window':
 *    Request object could not be cloned."
 *
 * Most extensions/scripts wrap `fetch` only — `XMLHttpRequest` stays clean.
 * This function returns a `Response`-shaped object so the rest of the code
 * doesn't need to branch.
 */
const xhrFallback = (url, config = {}) => new Promise((resolve, reject) => {
  try {
    const xhr = new XMLHttpRequest();
    xhr.open(config.method || 'GET', url, true);
    xhr.withCredentials = config.credentials !== 'omit';
    Object.entries(config.headers || {}).forEach(([k, v]) => {
      try { xhr.setRequestHeader(k, v); } catch (e) { /* unsafe header — skip */ }
    });
    xhr.onload = () => {
      // Build a minimal Response-compatible object. We re-use the global
      // Response constructor when the body type cooperates, falling back
      // to a duck-typed shim otherwise.
      const headers = new Headers();
      const raw = xhr.getAllResponseHeaders() || '';
      raw.split(/\r?\n/).forEach((line) => {
        const idx = line.indexOf(':');
        if (idx > 0) {
          const name = line.slice(0, idx).trim();
          const value = line.slice(idx + 1).trim();
          if (name) headers.append(name, value);
        }
      });
      try {
        resolve(new Response(xhr.responseText, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers,
        }));
      } catch (e) {
        // Older browsers refuse `new Response(...)` for some statuses (e.g. 204).
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          statusText: xhr.statusText,
          headers,
          text: async () => xhr.responseText,
          json: async () => JSON.parse(xhr.responseText || 'null'),
          clone: function () { return this; },
        });
      }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Network timeout'));
    xhr.send(config.body || null);
  } catch (err) {
    reject(err);
  }
});

const _isFetchWrapClone = (err) => {
  const msg = (err && (err.message || err.toString())) || '';
  // Chrome / Firefox / Safari all phrase this nearly identically.
  return /postMessage|could not be cloned|DataCloneError/i.test(msg);
};

// Helper function for API calls
const fetchApi = async (endpoint, options = {}) => {
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
    ...options,
  };
  const url = `${API_BASE_URL}${endpoint}`;

  // Primary path: native fetch. If a 3rd-party script has wrapped fetch
  // and breaks on the Request body (see xhrFallback comment), we retry
  // via XHR which extensions/observability widgets rarely hook.
  let response;
  try {
    response = await fetch(url, config);
  } catch (err) {
    if (_isFetchWrapClone(err)) {
      // Single retry via XHR. If THAT also fails the error is genuine.
      console.debug('[api] fetch wrapper threw clone error, falling back to XHR:', err?.message);
      response = await xhrFallback(url, config);
    } else {
      throw err;
    }
  }
  // Clone eagerly — some runtime interceptors (service workers, telemetry,
  // browser extensions) consume the body right after fetch resolves.
  let cloneForError = null;
  try {
    cloneForError = response.clone();
  } catch (err) {
    // Body was already consumed by a middleware; fall back to status-only
    // error mapping below. Logged so debuggers can still see it in prod.
    console.debug('[api] response.clone() failed:', err?.message);
  }

  if (!response.ok) {
    // Reasonable status-based defaults so the UI is useful even if a
    // middleware has already consumed the body.
    const defaultByStatus = {
      400: 'Bad request',
      401: 'Please sign in to continue',
      402: 'This is a Pro feature. Upgrade to unlock.',
      403: "You don't have access to this resource",
      404: 'Not found',
      409: 'Already exists',
      429: 'Rate limit hit — please retry in a moment',
      500: 'Server error. Please try again.',
    };
    let detail = defaultByStatus[response.status] || `HTTP ${response.status}`;
    if (cloneForError) {
      try {
        const raw = await cloneForError.text();
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (parsed.detail) detail = parsed.detail;
            else if (parsed.message) detail = parsed.message;
          } catch (err) {
            // Response wasn't JSON — fall back to raw text as the user-facing
            // detail. Debug-log so a malformed JSON blob is still discoverable.
            console.debug('[api] non-JSON error body, using raw:', err?.message);
            detail = raw;
          }
        }
      } catch (readErr) {
        // Body on the clone was already consumed too — keep the status-based
        // default. Log in case this masks a real server payload.
        console.debug('[api] clone body read failed:', readErr?.message);
      }
    }
    const err = new Error(detail);
    err.status = response.status;
    throw err;
  }

  return response.json();
};

// Auth API (Email/Password)
export const authApi = {
  register: (userData) => fetchApi('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  }),

  login: (credentials) => fetchApi('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  }),

  getProfile: () => fetchApi('/api/auth/profile'),

  logout: () => fetchApi('/api/auth/logout', { method: 'POST' }),

  // Passwordless magic-link auth.
  requestMagicLink: (email, referralCode = null) => fetchApi('/api/auth/magic-link/request', {
    method: 'POST',
    body: JSON.stringify({ email, referral_code: referralCode }),
  }),

  verifyMagicLink: (token) => fetchApi('/api/auth/magic-link/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  }),
};

// Google OAuth API
export const googleAuthApi = {
  // Exchange session_id for user data and set session cookie
  exchangeSession: (sessionId) => fetchApi('/api/auth/google/session', {
    method: 'POST',
    body: JSON.stringify({ session_id: sessionId }),
  }),
  
  // Get current authenticated user (works with cookie)
  getCurrentUser: () => fetchApi('/api/auth/google/me'),
  
  // Logout and clear session
  logout: () => fetchApi('/api/auth/google/logout', {
    method: 'POST',
  }),
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Initiate Google OAuth login - redirects to Google
  initiateLogin: () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  },
};

// Rooms API
export const roomsApi = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.difficulty) params.append('difficulty', filters.difficulty);
    if (filters.category) params.append('category', filters.category);
    if (filters.search) params.append('search', filters.search);
    const queryString = params.toString();
    return fetchApi(`/api/rooms${queryString ? `?${queryString}` : ''}`);
  },
  
  getById: (roomId) => fetchApi(`/api/rooms/${roomId}`),
  
  start: (roomId) => fetchApi(`/api/rooms/${roomId}/start`, {
    method: 'POST',
  }),
  
  submitFlag: (roomId, flag, taskId = null) => fetchApi(`/api/rooms/${roomId}/submit`, {
    method: 'POST',
    body: JSON.stringify({ flag, taskId }),
  }),
  
  getProgress: (roomId) => fetchApi(`/api/rooms/${roomId}/progress`),
};

// Learning Paths API
export const pathsApi = {
  getAll: () => fetchApi('/api/paths'),
  getById: (pathId) => fetchApi(`/api/paths/${pathId}`),
};

// Leaderboard API
export const leaderboardApi = {
  getAll: (limit = 100) => fetchApi(`/api/leaderboard?limit=${limit}`),
  getTop: (count = 10) => fetchApi(`/api/leaderboard/top/${count}`),
};

// AI API
export const aiApi = {
  analyzeThreat: (logs, context = '') => fetchApi('/api/ai/analyze-threat', {
    method: 'POST',
    body: JSON.stringify({ logs, context }),
  }),
  
  detectAnomaly: (data, type) => fetchApi('/api/ai/detect-anomaly', {
    method: 'POST',
    body: JSON.stringify({ data, type }),
  }),
  
  generateScenario: (difficulty, topic) => fetchApi('/api/ai/generate-scenario', {
    method: 'POST',
    body: JSON.stringify({ difficulty, topic }),
  }),

  roomHint: (roomId, taskId = null, question = null) => fetchApi('/api/ai/room-hint', {
    method: 'POST',
    body: JSON.stringify({ room_id: roomId, task_id: taskId, question }),
  }),

  getHintQuota: () => fetchApi('/api/ai/hint-quota'),

  getAnalyses: () => fetchApi('/api/ai/analyses'),
};

// Badges API
export const badgesApi = {
  getAll: () => fetchApi('/api/badges'),
  getUserBadges: () => fetchApi('/api/badges/user'),
};

// Payments API (Stripe)
export const paymentsApi = {
  getPackages: () => fetchApi('/api/payments/packages'),

  createCheckout: (packageId) => fetchApi('/api/payments/checkout', {
    method: 'POST',
    body: JSON.stringify({
      package_id: packageId,
      origin_url: window.location.origin,
    }),
  }),

  getStatus: (sessionId) => fetchApi(`/api/payments/status/${sessionId}`),

  getSubscription: () => fetchApi('/api/payments/subscription'),

  startTrial: () => fetchApi('/api/payments/start-trial', { method: 'POST' }),

  cancel: () => fetchApi('/api/payments/cancel', { method: 'POST' }),

  // Loyalty conversion offer — $12 first month for returning Pro users in
  // the renewal/win-back window. Server-authoritative: this endpoint will
  // return `eligible: false` if the user doesn't qualify.
  loyaltyEligibility: () => fetchApi('/api/payments/loyalty-eligibility'),
};

// Waitlist API
export const waitlistApi = {
  joinTeams: (payload) => fetchApi('/api/waitlist/teams', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  teamsCount: () => fetchApi('/api/waitlist/teams/count'),
};

// Teams API — org seat-based subscriptions
export const teamsApi = {
  create: (orgName, seats) => fetchApi('/api/teams/create', {
    method: 'POST',
    body: JSON.stringify({
      org_name: orgName,
      seats,
      origin_url: window.location.origin,
    }),
  }),
  paymentConfirm: (sessionId) => fetchApi(`/api/teams/payment-confirm/${sessionId}`, {
    method: 'POST',
  }),
  me: () => fetchApi('/api/teams/me'),
  invite: (note = null) => fetchApi('/api/teams/invite', {
    method: 'POST',
    body: JSON.stringify({ note }),
  }),
  invitePreview: (token) => fetchApi(`/api/teams/accept/${token}`),
  acceptInvite: (token) => fetchApi(`/api/teams/accept/${token}`, { method: 'POST' }),
  removeMember: (memberUserId) => fetchApi(`/api/teams/member/${memberUserId}`, {
    method: 'DELETE',
  }),
  // CSV is downloaded directly via <a href> with credentials, see Teams.jsx
  complianceCsvUrl: () => `${API_BASE_URL}/api/teams/compliance.csv`,
};

// Challenges API
export const challengesApi = {
  getDaily: () => fetchApi('/api/challenges/daily'),
};

// Referrals API
export const referralsApi = {
  getMe: () => fetchApi('/api/referrals/me'),
  apply: (code) => fetchApi('/api/referrals/apply', {
    method: 'POST',
    body: JSON.stringify({ code }),
  }),
};

// Profile (skill radar + certificates) API
export const profileApi = {
  getSkillRadar: () => fetchApi('/api/profile/skill-radar'),
  getCertificate: (roomId) => fetchApi(`/api/profile/certificate/${roomId}`),
};

// Sandbox Credits API — Free-tier session credit engine
export const sandboxApi = {
  getCredits: () => fetchApi('/api/sandbox/credits'),
  getHistory: () => fetchApi('/api/sandbox/credits/history'),
};

/**
 * Behavior-event tracking — frontend-originated events only.
 * Allowed event_type values are gated server-side (CLIENT_EMITTED_EVENTS).
 * Fire-and-forget; never blocks the caller and swallows failures so the
 * UI is unaffected if the analytics pipe is down.
 */
export const eventsApi = {
  track: (eventType, payload = {}, source = null) => {
    try {
      const body = JSON.stringify({
        event_type: eventType,
        payload,
        ...(source ? { source } : {}),
      });
      // Use sendBeacon when available for session_end so it survives unload.
      if (eventType === 'session_end' && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/events/track`, blob);
        return Promise.resolve({ ok: true, beacon: true });
      }
      return fetchApi('/api/events/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }).catch((e) => {
        console.debug('[events] track failed:', e?.message);
        return { ok: false };
      });
    } catch (e) {
      console.debug('[events] track threw:', e?.message);
      return Promise.resolve({ ok: false });
    }
  },
};
